import {
  Deal,
  OrderValues,
  PackageConfig,
  PriceValue,
  RemoteConfig,
} from '@ca/common/types'
import {
  Bundle,
  BundleType,
  OrderFrame,
  OrderItem,
  StepsState,
} from '@order/orderTypes'
import { Discounts } from '../discounts/Discounts'
import { debugOrder } from '../getDebugLog'
import { getPackageVariant } from '../packages/PackagesUtils'
import { OrderElement, OrderElementExtra } from './getOrderPriceSummary'
import FrameUtils from '@frame/FrameUtils'

export interface BundleSummaryItem extends Bundle {
  id: string
  summary: 'bundle'
  bundleType: BundleType
  deal?: Deal
  /**
   * First frame in the bundle
   */
  frame: OrderFrame | undefined
  packageCfg?: PackageConfig
  packageVariantId?: string
}

export interface OrderSummaryItem extends OrderItem {
  summary: 'frame'
  deal?: Deal
  steps: StepsState
}

export type SummableItem = OrderSummaryItem | BundleSummaryItem

export const getOrderItemSummary = (
  item: SummableItem,
  config: RemoteConfig,
  values: OrderValues
) => {
  const elements = new Map<string, OrderElement>()

  // If the item has a deal and a package we need to show the deal and the upgrade

  if (item.summary === 'bundle' && item.deal && item.packageCfg) {
    const packageVariant = getPackageVariant(item.packageCfg, config, values)
    const packageCfg = packageVariant || item.packageCfg
    const variantPrice =
      PriceValue.get(packageCfg.price, config.region) ??
      PriceValue.safe(item.packageCfg.price, config.region)

    const basePackageID = item.deal.packages.find(
      (id) => config.packages[id]?.type === item.packageCfg?.type
    )

    const basePackage = basePackageID
      ? config.packages[basePackageID]
      : undefined

    const baseVariant = getPackageVariant(basePackage, config, values)

    const dealPrice =
      PriceValue.get(baseVariant?.price ?? basePackage?.price, config.region) ??
      0

    const upgradePrice = Math.max(variantPrice - dealPrice, 0)

    const isBase = item.deal.packages.includes(item.packageCfg.id)
    const isVariantUpgrade = variantPrice > dealPrice

    const dealConfig = {
      variable: 'deal',
      value: item.deal.code,
      price: Discounts.wrap(dealPrice),
      summary: true,
      extra: {
        deal: item.deal,
      },
    }
    elements.set('deal', dealConfig)

    // If the package is base we don't have an upgrade and only show the deal and use its price
    if (!isBase || isVariantUpgrade) {
      const upgradeConfig = {
        variable: 'upgrade',
        value: item.packageCfg.id,
        extra: {
          variant: packageVariant?.value,
        },
        price: Discounts.wrap(upgradePrice),
        summary: true,
      }
      elements.set('upgrade', upgradeConfig)
    }
  }

  if (item.summary === 'frame' && item.frame) {
    const price = FrameUtils.getPrice({
      frame: item.frame,
      values,
      config,
    })
    const framePrice = Discounts.wrap(price)
    // Should we handle missing frame at this point?
    const frameConfig = {
      variable: 'frame',
      value: item.frame.id,
      price: framePrice,
      summary: true,
    }
    elements.set('frame', frameConfig)
  }

  item.steps.forEach((step) => {
    if (!step.var || !step.val) {
      return
    }

    const variableConfig = config.variables?.[step.var]

    const option = config.items[step.oId]
    if (!option) {
      debugOrder.warn('option not found', step.oId, config.items)
      return
    }
    const variant =
      option.base.id.toString() === step.vId
        ? option.base
        : option.variants.find((v) => v.id.toString() === step.vId)
    if (!variant) {
      debugOrder.warn('variant not found', step, option.variants, option)
      return
    }
    const price = PriceValue.safe(variant.price, config.region)

    const elementConfig = {
      variable: step.var,
      value: step.val,
      summary: !!variableConfig?.summary,
      price: Discounts.wrap(price),
      extra: {
        translation: {
          path: variant.translationPath,
          type: values.values['type'],
        },
      } satisfies OrderElementExtra,
    }
    elements.set(step.var, elementConfig)
  })

  return elements
}
